import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const SEEN_KEY = 'regen_intro_seen';
export const introSeen = () => localStorage.getItem(SEEN_KEY) === '1';

// Full-screen design images with invisible hotspots layered over the baked-in buttons.
// Positions are % of the image box (source images are 941 x 1672).
const SCREENS = [
  {
    src: '/onboarding/intro1.png',
    hotspots: [
      { tid: 'intro-skip', to: '/login', t: 0.5, l: 79, w: 20, h: 6 },
      { tid: 'intro-signin', to: '/login', t: 88, l: 55, w: 22, h: 4.5 },
    ],
  },
  {
    src: '/onboarding/intro2.png',
    hotspots: [
      { tid: 'intro-skip-2', to: '/login', t: 0.5, l: 79, w: 20, h: 6 },
      { tid: 'intro-signin-2', to: '/login', t: 89, l: 56, w: 22, h: 4.5 },
    ],
  },
  {
    src: '/onboarding/intro3.png',
    hotspots: [
      { tid: 'intro-create-account', to: '/register', t: 89, l: 9, w: 82, h: 5.5 },
      { tid: 'intro-signin-3', to: '/login', t: 93, l: 53, w: 20, h: 4 },
    ],
  },
];

export default function IntroOnboarding() {
  const nav = useNavigate();
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const startX = useRef(null);

  const done = () => localStorage.setItem(SEEN_KEY, '1');
  const go = (n) => { if (n < 0 || n > 2) return; setDir(n > i ? 1 : -1); setI(n); };
  const goTo = (to) => { done(); nav(to); };

  const onTouchStart = (e) => { startX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (dx < -50) go(i + 1); else if (dx > 50) go(i - 1);
    startX.current = null;
  };

  const variants = {
    enter: (d) => ({ x: d > 0 ? 60 : -60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: d > 0 ? -60 : 60, opacity: 0 }),
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-hidden bg-white"
         style={{ height: '100dvh' }}
         data-testid="intro-onboarding"
         onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="relative mx-auto"
           style={{ height: 'min(100dvh, 100vw * 1672 / 941)', aspectRatio: '941 / 1672' }}>
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div key={i} custom={dir} variants={variants} initial="enter" animate="center" exit="exit"
                      transition={{ duration: 0.28, ease: 'easeOut' }}
                      className="absolute inset-0"
                      onClick={() => go(i + 1)}>
            <img src={SCREENS[i].src} alt={`YourReGen intro screen ${i + 1}`}
                 className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
                 draggable={false} />
            {SCREENS[i].hotspots.map((h) => (
              <button key={h.tid} data-testid={h.tid} aria-label={h.tid}
                      onClick={(e) => { e.stopPropagation(); goTo(h.to); }}
                      className="absolute cursor-pointer bg-transparent"
                      style={{ top: `${h.t}%`, left: `${h.l}%`, width: `${h.w}%`, height: `${h.h}%` }} />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
